import React, { useContext, useState, useEffect } from 'react'
import { Container, Grid, Paper, Typography, Box, Tabs, Tab, IconButton, Stack, Button, Tooltip, Avatar, Skeleton, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ShoppingCartContext, { ShoppingCartProvider } from './ShoppingCartContext'
import { Add, ShoppingCartOutlined, Close, Person, AccountBalanceWalletOutlined } from '@mui/icons-material'
import Copyright from '../../General/Copyright'
import Session from './Session'
import InfoCarousel from './InfoCarousel'
import CostumerForm from './CostumerForm'

const UserAccountHeader = ({ user, balance, loadingBalance }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const userPermissions = JSON.parse(sessionStorage.getItem('permissions'))

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2.5,
        mb: 3,
        background: theme => (theme.palette.mode === 'light' ? 'linear-gradient(135deg, rgba(255, 87, 34, 0.05) 0%, rgba(135, 3, 0, 0.05) 100%)' : 'linear-gradient(135deg, rgba(255, 87, 34, 0.15) 0%, rgba(135, 3, 0, 0.15) 100%)'),
        border: '1px solid rgba(255, 87, 34, 0.2)',
        borderRadius: 3,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(255, 87, 34, 0.12)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems='center'
        justifyContent='space-between'
      >
        <Grid
          item
          xs={12}
          sm={8}
        >
          <Stack
            direction='row'
            spacing={2}
            alignItems='center'
          >
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: theme.palette.primary.main,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(255, 87, 34, 0.3)'
              }}
            >
              <Person sx={{ fontSize: 28 }} />
            </Avatar>
            <Stack>
              <Typography
                variant='h6'
                fontWeight='bold'
                sx={{ color: theme.palette.text.primary }}
              >
                {user?.name || 'Usuario'}
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
              >
                Rol: {userPermissions?.role || 'Cliente'}
              </Typography>
            </Stack>
          </Stack>
        </Grid>

        {['Admin', 'PDV', 'Supervisor', 'CompanyAdmin', 'Cliente'].includes(userPermissions?.role) && (
          <Grid
            item
            xs={12}
            sm={4}
            onClick={() => navigate('/addCredits')}
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              borderRadius: 2,
              p: 1,
              '&:hover': {
                backgroundColor: theme => (theme.palette.mode === 'light' ? 'rgba(0, 122, 3, 0.05)' : 'rgba(0, 122, 3, 0.15)'),
                transform: 'scale(1.02)'
              }
            }}
          >
            <Stack
              direction='row'
              spacing={1.5}
              alignItems='center'
              justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'rgba(0, 122, 3, 0.1)',
                  color: '#007a03'
                }}
              >
                <AccountBalanceWalletOutlined />
              </Avatar>
              <Stack>
                <Typography
                  variant='caption'
                  sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  Saldo Disponible
                </Typography>
                {loadingBalance ? (
                  <Skeleton
                    width={100}
                    height={32}
                  />
                ) : (
                  <Typography
                    variant='h5'
                    fontWeight='bold'
                    sx={{ color: '#007a03' }}
                  >
                    ${balance !== undefined && balance !== null ? Number(balance).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Grid>
        )}
      </Grid>
    </Paper>
  )
}

function Carts() {
  /* useContext */
  const { carts, addCart, actualCart, setActualCart, removeCart, showSessionDelete, setShowSessionDelete } = useContext(ShoppingCartContext)

  /* useState */
  const [showCostumerForm, setShowCostumerForm] = useState(false)
  const [creatingQuote, setCreatingQuote] = useState(false)
  const [balance, setBalance] = useState(undefined)
  const [loadingBalance, setLoadingBalance] = useState(false)

  /* items from sessionStorage */
  const permissions = JSON.parse(sessionStorage.getItem('permissions'))
  const user = JSON.parse(sessionStorage.getItem('user'))
  const localhost = sessionStorage.getItem('localhost')
  const token = sessionStorage.getItem('token')

  async function getBalance() {
    try {
      setLoadingBalance(true)
      const request = await fetch(`${localhost}/api/v2/payment/credit`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (request.ok) {
        const response = await request.json()
        setBalance(response.balance)
      }
    } catch (error) {
      console.error('Get balance in Carts:', error)
    } finally {
      setLoadingBalance(false)
    }
  }

  /* useEffect */
  useEffect(() => {
    if (['Admin', 'PDV', 'Supervisor'].includes(permissions['role']) && carts.length === 0) setShowCostumerForm(true)
    getBalance()
  }, [])

  return (
    <>
      <Container
        maxWidth='lg'
        sx={{ mt: 4, mb: 4, textAlign: 'left' }}
      >
        <UserAccountHeader
          user={user}
          balance={balance}
          loadingBalance={loadingBalance}
        />
        <InfoCarousel></InfoCarousel>
        <Grid
          backgroundColor='default'
          container
          spacing={1}
        >
          <Grid
            backgroundColor='default'
            item
            xs={12}
            md={12}
          >
            <Paper
              variant='outlined'
              sx={{ p: 2, backgroundColor: 'default', minHeight: 320 }}
            >
              {/*'Mi carrito'*/}
              <Stack backgroundColor='default'>
                <Typography
                  variant='h1'
                  sx={{ paddingY: 2, textAlign: 'left' }}
                >
                  Mis carritos
                </Typography>
                {carts.length === 0 ? (
                  <Stack
                    backgroundColor='default'
                    //spacing={100} //espacio de boton
                    //sx={{ p: 2 }}
                  >
                    <Stack
                      backgroundColor='default'
                      alignItems='center'
                      direction='column'
                      //spacing={1}
                      //marginTop={2}
                    >
                      <ShoppingCartOutlined sx={{ fontSize: 50 }} />
                      <Stack backgroundColor='default'>
                        <Typography variant='subtitle1'>Sin Carritos</Typography>
                        <Typography variant='body1'>Para poder empezar, es necesario que inicies un carrito</Typography>
                      </Stack>
                    </Stack>
                    {/*Boton crear sesion*/}
                    <Stack
                      backgroundColor='default'
                      direction='row'
                      justifyContent='end'
                    >
                      <Button
                        variant='contained'
                        disabled={creatingQuote}
                        onClick={() => {
                          if (!['Admin', 'PDV', 'Supervisor'].includes(permissions['role'])) {
                            setCreatingQuote(true)
                          }

                          if (['Admin', 'PDV', 'Supervisor'].includes(permissions['role'])) {
                            setShowCostumerForm(true)
                            return
                          }

                          addCart()
                          setCreatingQuote(false)
                        }}
                      >
                        Crear carrito
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Paper variant='outlined'>
                    <Stack>
                      <Stack
                        direction='row'
                        justifyContent='space-between'
                        alignItems='center'
                        sx={{ boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.2)' }}
                      >
                        <Tabs
                          value={actualCart}
                          onChange={(event, index) => setActualCart(index)}
                          variant='scrollable'
                          visibleScrollbar={true}
                          scrollButtons={false}
                          allowScrollButtonsMobile
                        >
                          {carts.map((session, index) => (
                            <Tab
                              key={index}
                              label={
                                <Stack
                                  direction='row'
                                  alignItems='center'
                                  spacing={0.5}
                                >
                                  <span>{`Carrito ${index + 1}`}</span>
                                  <IconButton
                                    size='small'
                                    component='div'
                                    onClick={() => setShowSessionDelete(true)}
                                  >
                                    <Close sx={{ fontSize: '1rem' }} />
                                  </IconButton>
                                </Stack>
                              }
                            />
                          ))}
                        </Tabs>

                        <Tooltip
                          title={carts.length >= 10 ? 'Solo puedes tener 10 sesiones' : 'Agregar Carrito'}
                          placement='left'
                          arrow
                        >
                          <Box>
                            <IconButton
                              disabled={carts.length >= 10 || creatingQuote}
                              onClick={() => {
                                if (!['Admin', 'PDV', 'Supervisor'].includes(permissions['role'])) {
                                  setCreatingQuote(true)
                                }

                                if (['Admin', 'PDV', 'Supervisor'].includes(permissions['role'])) {
                                  setShowCostumerForm(true)
                                  return
                                }

                                addCart()
                                setCreatingQuote(false)
                              }}
                              sx={{
                                color: '#ff4500'
                              }}
                            >
                              <Add />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      </Stack>
                      {carts[actualCart] && <Session key={carts[actualCart].cart_uuid || actualCart} />}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          sx={{
            p: 1,
            marginTop: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 50,
            width: 'auto'
          }}
        >
          <Copyright sx={{ pt: 4 }} />
        </Paper>
      </Container>

      <CostumerForm
        displayState={[showCostumerForm, setShowCostumerForm]}
        addCart={addCart}
        setCreatingQuote={setCreatingQuote}
      />
    </>
  )
}

export default function MyShoppingCart() {
  return (
    <ShoppingCartProvider>
      <Carts />
    </ShoppingCartProvider>
  )
}
